import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PublicHeader from '../../components/navigation/PublicHeader';
import LoginForm from './components/LoginForm';
import MagicLinkStatus from './components/MagicLinkStatus';
import SecurityBadges from './components/SecurityBadges';
import FirstTimeUserGuide from './components/FirstTimeUserGuide';
import ConnectionStatus from './components/ConnectionStatus';
import { supabase } from '../../lib/supabase';

const AdminAuthenticationPortal = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [authState, setAuthState] = useState('login');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Show error passed back from /auth/callback (only once)
    const callbackError = location?.state?.error;
    if (callbackError) {
      setError(callbackError);

      // Clear router state so it doesn’t repeat on refresh/back
      navigate(location.pathname, { replace: true, state: null });
    }

    // Check existing session
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data?.session) {
        navigate('/fleet-operations-dashboard', { replace: true });
      }
    };

    checkSession();

    // Listen for sign-in events (magic link success)
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        navigate('/fleet-operations-dashboard', { replace: true });
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [navigate, location.pathname, location.state]);

  const handleLoginSubmit = async (email) => {
    setIsLoading(true);
    setError('');

    try {
  const redirectTo = `${window.location.origin}/auth/callback`;

const { error } = await supabase.auth.signInWithOtp({
  email,
  options: { emailRedirectTo: redirectTo },
});

      if (error) throw error;

      setSubmittedEmail(email);
      setAuthState('sent');
    } catch (err) {
      setError(err?.message || 'Authentication failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendLink = async () => {
    setIsLoading(true);
    setError('');

    try {
   const redirectTo = `${window.location.origin}/auth/callback`;

const { error } = await supabase.auth.signInWithOtp({
  email,
  options: { emailRedirectTo: redirectTo },
});

      if (error) throw error;

      alert(`Magic link resent to ${submittedEmail}`);
    } catch (err) {
      setError(err?.message || 'Failed to resend link.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setAuthState('login');
    setSubmittedEmail('');
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      <main className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold">Admin Portal</h1>
              <p className="text-muted-foreground">
                Secure access to RPM Diesel Fleet Management
              </p>
            </div>

            <ConnectionStatus />

            <div className="bg-card p-6 rounded-lg border">
              {authState === 'login' ? (
                <>
                  {error && <div className="mb-4 text-sm text-red-600">{error}</div>}
                  <LoginForm onSubmit={handleLoginSubmit} isLoading={isLoading} />
                </>
              ) : (
                <MagicLinkStatus
                  email={submittedEmail}
                  onResend={handleResendLink}
                  onBack={handleBackToLogin}
                />
              )}
              <SecurityBadges />
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-card p-6 rounded-lg border">
              <button
                onClick={() => setShowGuide(!showGuide)}
                className="text-primary text-sm mb-2"
              >
                {showGuide ? 'Hide Guide' : 'Show Guide'}
              </button>
              {showGuide && <FirstTimeUserGuide />}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminAuthenticationPortal;